import { OBJECT_NAMES, type ObjectName } from './objects';

/** Everything that carries object-level permissions: registry objects plus activities. */
export const PERMISSION_OBJECTS = [...OBJECT_NAMES, 'activity', 'product', 'campaign'] as const;
export type PermObject = (typeof PERMISSION_OBJECTS)[number];

/** Object-level permissions, modeled on CRUD + "view all" / "modify all" overrides. */
export interface ObjectPermissions {
  read: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
  /** Read every record of the object regardless of ownership and sharing. */
  viewAll: boolean;
  /** Read, edit and delete every record regardless of ownership and sharing. */
  modifyAll: boolean;
}

/** presalesWork: take handoffs (acknowledge, request documents, mark the quotation ready).
 * editQuotedBrands: record our quoted brands on deals assigned or shared to the user, even when not the deal's owner.
 * viewKpis: the KPIs tab (administrators and managers).
 * manageProjects: run every project after a deal is won (team, dates, costs, billing).
 * workProjects: work the projects one is assigned to.
 * accountsWork: the accounts department (invoice requests, approved purchases and won deals to do in Odoo).
 * logisticsWork: logistics (shipments of project materials from suppliers to site or warehouse). */
export const SYSTEM_PERMISSIONS = ['manageUsers', 'manageSetup', 'viewAuditLog', 'exportData', 'apiAccess', 'editQuotedBrands', 'presalesWork', 'viewKpis', 'manageProjects', 'workProjects', 'accountsWork', 'logisticsWork'] as const;
export type SystemPermission = (typeof SYSTEM_PERMISSIONS)[number];

export interface PermissionGrant {
  objects: Partial<Record<PermObject, Partial<ObjectPermissions>>>;
  system: Partial<Record<SystemPermission, boolean>>;
}

export interface EffectivePermissions {
  objects: Record<PermObject, ObjectPermissions>;
  system: Record<SystemPermission, boolean>;
}

/** Organization-wide default access for records a user does not own. */
/** `controlled_by_parent` applies to contacts only: access follows the contact's account. */
export type SharingModel = 'private' | 'public_read' | 'public_read_write' | 'controlled_by_parent';

const NONE: ObjectPermissions = { read: false, create: false, edit: false, delete: false, viewAll: false, modifyAll: false };

/**
 * Permissions are additive: the effective set is the union of every assigned grant.
 * Implications are normalised so callers can check a single flag.
 */
export function mergeGrants(grants: readonly PermissionGrant[]): EffectivePermissions {
  const objects = Object.fromEntries(PERMISSION_OBJECTS.map((o) => [o, { ...NONE }])) as Record<PermObject, ObjectPermissions>;
  const system = Object.fromEntries(SYSTEM_PERMISSIONS.map((p) => [p, false])) as Record<SystemPermission, boolean>;
  for (const g of grants) {
    for (const o of PERMISSION_OBJECTS) {
      const p = g.objects[o];
      if (!p) continue;
      const t = objects[o];
      for (const k of Object.keys(NONE) as (keyof ObjectPermissions)[]) if (p[k]) t[k] = true;
    }
    for (const s of SYSTEM_PERMISSIONS) if (g.system[s]) system[s] = true;
  }
  for (const o of PERMISSION_OBJECTS) {
    const t = objects[o];
    if (t.modifyAll) Object.assign(t, { read: true, edit: true, delete: true, viewAll: true });
    if (t.viewAll) t.read = true;
    if (t.edit || t.create || t.delete) t.read = true;
  }
  return { objects, system };
}

const ALL: ObjectPermissions = { read: true, create: true, edit: true, delete: true, viewAll: true, modifyAll: true };
const STANDARD: Partial<ObjectPermissions> = { read: true, create: true, edit: true, delete: true };

/** Permission sets created for every new organization. Admins can edit or add more. */
export const DEFAULT_PERMISSION_SETS: { key: string; name: string; description: string; grant: PermissionGrant }[] = [
  {
    key: 'system_admin',
    name: 'System Administrator',
    description: 'Full access to all records, users and setup.',
    grant: {
      objects: Object.fromEntries(PERMISSION_OBJECTS.map((o) => [o, ALL])),
      system: Object.fromEntries(SYSTEM_PERMISSIONS.map((p) => [p, true])),
    },
  },
  {
    key: 'sales_user',
    name: 'Sales User',
    description: 'Create and work their own accounts, contacts, leads and opportunities.',
    grant: { objects: { ...Object.fromEntries(PERMISSION_OBJECTS.map((o) => [o, STANDARD])), product: { read: true }, campaign: { read: true } }, system: {} },
  },
  {
    key: 'sales_manager',
    name: 'Sales Manager',
    description: 'Sales User plus read access to every record for coaching and forecasting.',
    grant: {
      objects: { ...Object.fromEntries(PERMISSION_OBJECTS.map((o) => [o, { ...STANDARD, viewAll: true }])), product: { read: true }, campaign: { ...STANDARD, viewAll: true } },
      system: { exportData: true, apiAccess: true, viewKpis: true },
    },
  },
  {
    key: 'presales_engineer',
    name: 'Pre Sales Engineer',
    description: 'Supports sales: sees every account, contact, lead and deal; works the deals they own or that are shared with them.',
    grant: {
      objects: {
        account: { ...STANDARD, viewAll: true }, contact: { ...STANDARD, viewAll: true }, lead: { ...STANDARD, viewAll: true },
        opportunity: { ...STANDARD, viewAll: true }, activity: STANDARD, product: { read: true }, campaign: { read: true },
      },
      system: { editQuotedBrands: true, presalesWork: true },
    },
  },
  {
    key: 'presales_manager',
    name: 'Pre Sales Manager',
    description: 'Leads pre-sales: sees everything, edits every deal, and manages products and prices.',
    grant: {
      objects: {
        account: { ...STANDARD, viewAll: true }, contact: { ...STANDARD, viewAll: true }, lead: { ...STANDARD, viewAll: true },
        opportunity: ALL, activity: { ...STANDARD, viewAll: true }, product: { ...STANDARD, viewAll: true }, campaign: { read: true },
      },
      system: { exportData: true, apiAccess: true, editQuotedBrands: true, presalesWork: true, viewKpis: true },
    },
  },
  {
    key: 'project_manager',
    name: 'Project Manager',
    description: 'Projects only: runs every project after a deal is won (team, dates, costs, billing). No access to the rest of the CRM.',
    grant: {
      // Projects only: no CRM records.
      objects: {},
      system: { manageProjects: true, workProjects: true },
    },
  },
  {
    key: 'project_engineer',
    name: 'Project Engineer',
    description: 'Projects only: works the projects they are assigned to. No access to the rest of the CRM.',
    grant: {
      objects: {},
      system: { workProjects: true },
    },
  },
  {
    key: 'accounts',
    name: 'Accounts',
    description: 'Accounts department only: invoice requests, approved purchase requests and won deals to do in Odoo, and projects (read-only). No access to the rest of the CRM.',
    grant: { objects: {}, system: { accountsWork: true } },
  },
  {
    key: 'logistics',
    name: 'Logistics',
    description: 'Logistics only: shipments of project materials from suppliers to site or warehouse, and projects (read-only). No access to the rest of the CRM.',
    grant: { objects: {}, system: { logisticsWork: true } },
  },
  {
    key: 'read_only',
    name: 'Read Only',
    description: 'View records they can see. No changes.',
    grant: { objects: Object.fromEntries(PERMISSION_OBJECTS.map((o) => [o, { read: true }])), system: {} },
  },
];

export const DEFAULT_SHARING: Record<ObjectName, SharingModel> = {
  account: 'public_read',
  contact: 'public_read',
  lead: 'public_read_write',
  opportunity: 'private',
};

/**
 * Project managers and engineers who have nothing else: they see only the Projects area (no accounts,
 * contacts, deals, reports or setup). Anyone who also has another permission set keeps the full CRM.
 */
export function isProjectsOnly(p: { objects: Record<string, { read: boolean }>; system: Record<string, boolean> }): boolean {
  const projects = !!(p.system.manageProjects || p.system.workProjects || p.system.logisticsWork);
  const other = p.system.manageSetup || p.system.manageUsers
    || ['account', 'contact', 'lead', 'opportunity', 'campaign'].some((o) => p.objects[o]?.read);
  return projects && !other;
}

/** Logistics with nothing else: the Logistics page (as home) and projects (read-only). */
export function isLogisticsOnly(p: { objects: Record<string, { read: boolean }>; system: Record<string, boolean> }): boolean {
  return !!p.system.logisticsWork && !p.system.manageProjects && !p.system.workProjects && isProjectsOnly(p);
}

/**
 * Logistics without project, sales, accounts or setup rights: sees only operational project information —
 * never the customer PO / BOQ, prices, costs, margins, billing or commercial terms. Enforced on the server.
 */
export function isLogisticsRestricted(p: { objects: Record<string, { read: boolean }>; system: Record<string, boolean> }): boolean {
  return !!p.system.logisticsWork && !p.system.manageProjects && !p.system.workProjects && !p.system.manageSetup && !p.system.accountsWork
    && !['account', 'contact', 'lead', 'opportunity'].some((o) => p.objects[o]?.read);
}

/** The accounts department with nothing else: the Accounts tab and projects (read-only) only. */
export function isAccountsOnly(p: { objects: Record<string, { read: boolean }>; system: Record<string, boolean> }): boolean {
  const other = p.system.manageSetup || p.system.manageUsers || p.system.manageProjects || p.system.workProjects
    || ['account', 'contact', 'lead', 'opportunity', 'campaign'].some((o) => p.objects[o]?.read);
  return !!p.system.accountsWork && !other;
}